import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import WarehouseOutlinedIcon from "@mui/icons-material/WarehouseOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import "../../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../../assets/css/PrimeGrid.css";

import { saveAs } from "file-saver";

import { AlertContext } from "../../../../context/AlertContext";
import { AuthContext } from "../../../../context/AuthContext";
import { ConfiguracionContext } from "../../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../../theme/brand";
import Spinner from "../../Spinner";
import Barra from "../../Barra";

const exportColumns = [
  { title: "ID", dataKey: "id" },
  { title: "SKU", dataKey: "sku" },
  { title: "Nombre", dataKey: "nombre" },
  { title: "Almacen", dataKey: "almacen" },
  { title: "Stock", dataKey: "stock" },
];

export default function StocksMedicamentos() {
  const [globalFilter, setGlobalFilter] = useState(null);
  const [nuevoStock, setNuevoStock] = useState(null);
  const [selectedRow, setSelectedRow] = useState([]);
  const [visible, setVisible] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state } = useContext(AuthContext);
  const {
    statusAcceso,
    almacenes,
    almacen,
    configuracion,
    stocksMedicamentos,
    obtenerStocksMedicamentos,
    obtenerAcceso,
    obtenerAlmacenes,
    guardarAlmacen,
    guardarStocksMedicamentos,
    ajustarStockMedicamento,
  } = useContext(ConfiguracionContext);

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({ usuarioId: state.usuario.id, layout: "StocksMedicamentos" });
    }
    obtenerAlmacenes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error"
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const ajustarClick = () => {
    // vuelve a chequear el permiso específico de ajuste
    obtenerAcceso({
      usuarioId: state.usuario.id,
      layout: "AjustesStocksMedicamentos",
    });

    if (selectedRow && selectedRow.id) {
      setNuevoStock(null);
      setVisible(true);
    } else {
      toast.current.show({
        severity: "info",
        summary: "Stocks",
        detail: "Debe seleccionar un item.",
        life: 3000,
      });
    }
  };

  const handleClose = () => setVisible(false);

  const confirmarAjuste = () => {
    if (!nuevoStock || nuevoStock < 0) {
      mensajeAlerta("Stock de Medicamentos", "Nuevo stock es necesario !.", "error");
      return;
    }
    ajustarStockMedicamento({ selectedRow, nuevoStock, usuario: state.usuario });
    setTimeout(() => {
      if (almacen) obtenerStocksMedicamentos({ almacen });
    }, 2000);
    handleClose();
  };

  const exitClick = () => navigate("/");

  const exportPdf = () => {
    import("jspdf").then((jsPDF) => {
      import("jspdf-autotable").then(() => {
        const doc = new jsPDF.default(0, 0);
        doc.setFontSize(12);
        doc.text("Listado de Stock de Medicamentos", 60, 8);
        doc.autoTable(exportColumns, stocksMedicamentos || []);
        window.open(doc.output("bloburl"));
      });
    });
  };

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(stocksMedicamentos || []);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      saveAs(blob, `stocksdemedicamentos_export_${new Date().getTime()}.xlsx`);
    });
  };

  const cargando = statusAcceso === 0 || statusAcceso === 400;

  return (
    <Fragment>
      <Barra />
      <Toast ref={toast} />

      {cargando ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box id="dataTableBotones" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            {almacenes ? (
              <Fragment>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        <WarehouseOutlinedIcon />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          Stocks de Medicamentos
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {stocksMedicamentos ? `${stocksMedicamentos.length} registros` : "—"}
                        </Typography>
                      </Box>
                    </Stack>

                    <Autocomplete
                      options={almacenes}
                      disablePortal
                      getOptionLabel={(o) => (o && o.nombre) || ""}
                      onChange={(e, value) => {
                        if (value) {
                          obtenerStocksMedicamentos({ almacen: value.id });
                          guardarAlmacen(value.id);
                        } else {
                          guardarStocksMedicamentos([]);
                          guardarAlmacen(null);
                        }
                      }}
                      sx={{ minWidth: { xs: "100%", md: 260 } }}
                      renderInput={(params) => (
                        <TextField {...params} label="Almacén" size="small" />
                      )}
                    />

                    <TextField
                      size="small"
                      placeholder="Buscar…"
                      onInput={(e) => setGlobalFilter(e.target.value)}
                      sx={{ minWidth: { xs: "100%", md: 220 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<TuneOutlinedIcon />}
                      onClick={ajustarClick}
                      disabled={!selectedRow || !selectedRow.id}
                    >
                      Ajustar
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={exportExcel}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PictureAsPdfOutlinedIcon />}
                      onClick={exportPdf}
                    >
                      PDF
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="text"
                      color="inherit"
                      startIcon={<CloseIcon />}
                      onClick={exitClick}
                    >
                      Cerrar
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                  }}
                >
                  <DataTable
                    value={stocksMedicamentos || []}
                    selection={selectedRow}
                    onSelectionChange={(e) => setSelectedRow(e.value)}
                    selectionMode="single"
                    dataKey="id"
                    className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
                    globalFilter={globalFilter}
                    emptyMessage="Elegí un almacén para ver el stock."
                    paginator
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50]}
                  >
                    <Column field="id" header="ID" sortable style={{ width: "8rem" }} />
                    <Column field="sku" header="SKU" sortable style={{ width: "10rem" }} />
                    <Column field="nombre" header="Nombre" sortable />
                    <Column field="almacen" header="Almacén" sortable />
                    <Column field="stock" header="Stock" sortable style={{ width: "8rem" }} />
                  </DataTable>
                </Paper>
              </Fragment>
            ) : (
              <Spinner />
            )}
          </Box>

          <Dialog open={visible} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
              <TuneOutlinedIcon color="primary" />
              Ajuste de stock
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={6}>
                  <TextField
                    label="Stock actual"
                    fullWidth
                    size="small"
                    type="number"
                    disabled
                    value={selectedRow && selectedRow.stock != null ? selectedRow.stock : ""}
                    sx={{
                      "& .MuiInputBase-input.Mui-disabled": {
                        WebkitTextFillColor: "rgba(0,0,0,0.87)",
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Nuevo stock"
                    fullWidth
                    size="small"
                    type="number"
                    autoFocus
                    onChange={(e) => {
                      let v = e.target.value;
                      if (v && Number(v) < 0) v = "0";
                      setNuevoStock(v);
                    }}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={handleClose} color="inherit" startIcon={<CloseIcon />}>
                Salir
              </Button>
              <Button onClick={confirmarAjuste} variant="contained" startIcon={<CheckIcon />}>
                Confirmar
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
